import type { ReactElement } from 'react';

import type { ButtonProps, ButtonVariant, SubmitButtonProps } from '@capsule/consumer-design-system';
import { Typography, Button, SubmitButton } from '@capsule/consumer-design-system';

import type { AnimationProps } from 'framer-motion';
import { motion } from 'framer-motion';
import styled, { css } from 'styled-components';

const CTAFooter = styled(motion.footer)`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: ${({ theme }) => theme.space.s5};
  background-color: ${({ theme }) => theme.colors.white};
  flex-shrink: 0;

  /**
  * This shadow represents the elevation of the footer, but also a white fade above the footer to
  * help visually queue users to the fact that content exists and must be scrolled to.
  */
  box-shadow: ${({ theme }) => theme.shadows.dp24}, 0rem 0rem 0 0.5rem white, 0rem 0rem 2vw 6vw hsl(0deg 0% 100% / 0.8);
  z-index: 1;

  & > button {
    width: 100%;
  }

  ${({ theme }) => theme.mediaQueries.small} {
    box-shadow: none;
    padding: 0;
  }
`;

const ErrorContainer = styled(Typography).attrs({ variant: 'small' })`
  color: ${({ theme }) => theme.colors.capsuleRed60};
  text-align: center;

  transition: all ${({ theme }) => theme.animation.typical};
  height: 1.6rem;
  opacity: 1;
  margin-bottom: ${({ theme }) => theme.space.s3};

  &:empty {
    transition: height 0.2s linear;
    height: 0;
    opacity: 0;
    margin-bottom: 0;
  }
`;

export interface CTAGroupProps extends AnimationProps {
  className?: string;
  cta1: Omit<ButtonProps, 'variant'> | Omit<SubmitButtonProps, 'variant'>;
  cta2?:
    | (Omit<ButtonProps, 'variant'> & {
        /** @default "outline-lg" */
        variant?: Extract<ButtonVariant, 'contained-primary-lg' | 'contained-secondary-lg' | 'outline-lg' | 'text-md'>;
      })
    | ReactElement;
  error?: string;
}

export const CTAGroup = ({ className, cta1, cta2, error, ...animationProps }: CTAGroupProps) => (
  <CTAFooter className={className} {...animationProps}>
    <ErrorContainer role="alert">{error}</ErrorContainer>

    {'isSubmitting' in cta1 ? (
      <SubmitButton {...cta1} variant="contained-secondary-lg" />
    ) : (
      <Button {...cta1} variant="contained-secondary-lg" />
    )}

    {cta2 && 'onClick' in cta2 && (
      <Button
        {...cta2}
        css={css`
          margin-top: ${({ theme }) => theme.space.s3};
        `}
        variant={cta2?.variant ?? 'outline-lg'}
      />
    )}

    {cta2 && !('onClick' in cta2) && (
      <div
        css={css`
          margin-top: ${({ theme }) => theme.space.s3};
          width: 100%;
          height: 100%;
        `}
      >
        {cta2}
      </div>
    )}
  </CTAFooter>
);
